// TypeScript port of api-recorder-extension/src/swagger-parser.js
// Parses OpenAPI 3.x and Swagger 2.0 JSON specs into a compact LLM-ready text summary.

export interface SwaggerParseResult {
  text: string;
  endpointCount: number;
  title: string;
  baseUrl: string;
}

interface EndpointInfo {
  method: string;
  path: string;
  summary: string;
  description: string;
  tags: string;
  params: string[];
  requestBody: string | null;
  responses: string[];
}

// ── Entry point ───────────────────────────────────────────────────────────────

export function parseSwaggerSpec(specText: string): SwaggerParseResult {
  let spec: Record<string, unknown>;
  try {
    spec = JSON.parse(specText) as Record<string, unknown>;
  } catch {
    throw new Error('Could not parse the spec as JSON. Please provide a valid OpenAPI/Swagger JSON document.');
  }

  const isV3 = !!spec.openapi;
  const isV2 = !!spec.swagger;

  if (!isV3 && !isV2) {
    throw new Error('Unrecognised format. Expected an OpenAPI 3.x or Swagger 2.0 JSON document (must have an "openapi" or "swagger" field).');
  }

  return isV3 ? parseV3(spec) : parseV2(spec);
}

// ── OpenAPI 3.x parser ────────────────────────────────────────────────────────

function parseV3(spec: Record<string, unknown>): SwaggerParseResult {
  const info = (spec.info || {}) as Record<string, unknown>;
  const title = (info.title as string) || 'API';
  const version = (info.version as string) || '';
  const description = (info.description as string) || '';

  const servers = (spec.servers as Array<Record<string, unknown>>) || [];
  const baseUrl = (servers[0]?.url as string) || '';

  const endpoints: EndpointInfo[] = [];
  const paths = (spec.paths || {}) as Record<string, Record<string, unknown>>;
  const components = (spec.components || {}) as Record<string, unknown>;
  const schemas = (components.schemas || {}) as Record<string, unknown>;

  const HTTP_METHODS = ['get', 'post', 'put', 'delete', 'patch', 'options', 'head'];

  for (const [path, pathItem] of Object.entries(paths)) {
    for (const method of HTTP_METHODS) {
      const op = pathItem[method] as Record<string, unknown> | undefined;
      if (!op) continue;

      const summary = (op.summary as string) || (op.operationId as string) || '';
      const opDescription = (op.description as string) || '';
      const tags = ((op.tags as string[]) || []).join(', ');

      const pathParams = ((pathItem.parameters || []) as unknown[]) as Record<string, unknown>[];
      const opParams   = ((op.parameters   || []) as unknown[]) as Record<string, unknown>[];
      const allParams  = [...pathParams, ...opParams];
      const params = allParams.map(p => {
        const schema = (p.schema || {}) as Record<string, unknown>;
        const type = (schema.type as string) || (p.type as string) || 'string';
        const required = p.required ? ' [required]' : '';
        return `  - ${p.name} (${p.in}, ${type}${required}): ${p.description || ''}`;
      });

      let requestBody: string | null = null;
      if (op.requestBody) {
        const rb = op.requestBody as Record<string, unknown>;
        const content = (rb.content || {}) as Record<string, Record<string, unknown>>;
        const jsonContent = content['application/json'] || Object.values(content)[0];
        if (jsonContent) {
          const schemaText = schemaToText(jsonContent.schema as Record<string, unknown>, schemas, 0);
          const req = rb.required ? ' [required]' : '';
          requestBody = `  Body${req} (application/json):\n${schemaText}`;
        }
      }

      const opResponses = (op.responses || {}) as Record<string, Record<string, unknown>>;
      const responses = Object.entries(opResponses).map(([code, resp]) => {
        const desc = (resp.description as string) || '';
        const content = (resp.content || {}) as Record<string, Record<string, unknown>>;
        const jsonContent = content['application/json'];
        const schemaHint = jsonContent
          ? '\n' + schemaToText(jsonContent.schema as Record<string, unknown>, schemas, 2)
          : '';
        return `  ${code}: ${desc}${schemaHint}`;
      });

      endpoints.push({ method: method.toUpperCase(), path, summary, description: opDescription, tags, params, requestBody, responses });
    }
  }

  return formatEndpoints(title, version, description, baseUrl, endpoints);
}

// ── Swagger 2.x parser ────────────────────────────────────────────────────────

function parseV2(spec: Record<string, unknown>): SwaggerParseResult {
  const info = (spec.info || {}) as Record<string, unknown>;
  const title = (info.title as string) || 'API';
  const version = (info.version as string) || '';
  const description = (info.description as string) || '';

  const schemes = (spec.schemes as string[]) || ['https'];
  const host = (spec.host as string) || '';
  const basePath = (spec.basePath as string) || '';
  const baseUrl = host ? `${schemes[0]}://${host}${basePath}` : basePath;

  const endpoints: EndpointInfo[] = [];
  const paths = (spec.paths || {}) as Record<string, Record<string, unknown>>;
  const definitions = (spec.definitions || {}) as Record<string, unknown>;

  const HTTP_METHODS = ['get', 'post', 'put', 'delete', 'patch', 'options', 'head'];

  for (const [path, pathItem] of Object.entries(paths)) {
    for (const method of HTTP_METHODS) {
      const op = pathItem[method] as Record<string, unknown> | undefined;
      if (!op) continue;

      const summary = (op.summary as string) || (op.operationId as string) || '';
      const opDescription = (op.description as string) || '';
      const tags = ((op.tags as string[]) || []).join(', ');

      const params: string[] = [];
      let requestBody: string | null = null;

      for (const p of ((op.parameters || []) as unknown[]) as Record<string, unknown>[]) {
        if (p.in === 'body') {
          const schemaText = schemaToText(p.schema as Record<string, unknown>, definitions, 0);
          const req = p.required ? ' [required]' : '';
          requestBody = `  Body${req}:\n${schemaText}`;
        } else {
          const pSchema = (p.schema || {}) as Record<string, unknown>;
          const type = (p.type as string) || (pSchema.type as string) || 'string';
          const req = p.required ? ' [required]' : '';
          params.push(`  - ${p.name} (${p.in}, ${type}${req}): ${p.description || ''}`);
        }
      }

      const opResponses = (op.responses || {}) as Record<string, Record<string, unknown>>;
      const responses = Object.entries(opResponses).map(([code, resp]) => {
        const desc = (resp.description as string) || '';
        const schemaHint = resp.schema
          ? '\n' + schemaToText(resp.schema as Record<string, unknown>, definitions, 2)
          : '';
        return `  ${code}: ${desc}${schemaHint}`;
      });

      endpoints.push({ method: method.toUpperCase(), path, summary, description: opDescription, tags, params, requestBody, responses });
    }
  }

  return formatEndpoints(title, version, description, baseUrl, endpoints);
}

// ── Schema → text ─────────────────────────────────────────────────────────────

function schemaToText(schema: Record<string, unknown> | undefined, defs: Record<string, unknown>, indent: number): string {
  if (!schema) return '';
  const pad = '  '.repeat(indent + 1);

  if (schema.$ref) {
    const refName = (schema.$ref as string).split('/').pop()!;
    const resolved = defs[refName] as Record<string, unknown> | undefined;
    if (resolved) return schemaToText(resolved, defs, indent);
    return `${pad}$ref: ${refName}`;
  }

  if (schema.type === 'object' || schema.properties) {
    const props = (schema.properties || {}) as Record<string, Record<string, unknown>>;
    const required = new Set<string>((schema.required as string[]) || []);
    const lines = Object.entries(props).map(([key, val]) => {
      const req = required.has(key) ? ' [required]' : '';
      const type = (val.type as string) || (val.$ref ? (val.$ref as string).split('/').pop()! : 'object');
      const desc = val.description ? ` — ${val.description}` : '';
      return `${pad}${key}: ${type}${req}${desc}`;
    });
    return lines.join('\n') || `${pad}(empty object)`;
  }

  if (schema.type === 'array' && schema.items) {
    return `${pad}array of:\n${schemaToText(schema.items as Record<string, unknown>, defs, indent + 1)}`;
  }

  const type = (schema.type as string) || 'any';
  const enumHint = schema.enum ? ` (enum: ${(schema.enum as unknown[]).join(', ')})` : '';
  const format = schema.format ? ` (${schema.format})` : '';
  return `${pad}${type}${format}${enumHint}`;
}

// ── Format for LLM ────────────────────────────────────────────────────────────

function formatEndpoints(
  title: string,
  version: string,
  description: string,
  baseUrl: string,
  endpoints: EndpointInfo[]
): SwaggerParseResult {
  const lines: string[] = [
    `API: ${title}${version ? ' v' + version : ''}`,
    baseUrl ? `Base URL: ${baseUrl}` : '',
    description ? `Description: ${description}` : '',
    `Total endpoints: ${endpoints.length}`,
    '',
  ].filter(l => l !== undefined);

  for (const ep of endpoints) {
    lines.push(`${ep.method} ${ep.path}`);
    if (ep.summary)           lines.push(`  Summary: ${ep.summary}`);
    if (ep.tags)              lines.push(`  Tags: ${ep.tags}`);
    if (ep.description)       lines.push(`  Description: ${ep.description}`);
    if (ep.params.length)     { lines.push('  Parameters:'); lines.push(...ep.params); }
    if (ep.requestBody)       { lines.push('  Request Body:'); lines.push(ep.requestBody); }
    if (ep.responses.length)  { lines.push('  Responses:'); lines.push(...ep.responses); }
    lines.push('');
  }

  return { text: lines.join('\n'), endpointCount: endpoints.length, title, baseUrl };
}
